import { ParseResult, err, ok } from "../core/result";
import {
  Catalog,
  CatalogFacet,
  CatalogNodeEntry,
  CatalogPackEntry,
  CountLabels,
  packManifestToCatalog,
  parseCatalogJson,
} from "../core/catalog";
import { DEFAULT_LANGUAGE, localizedFile } from "../core/i18n";
import { NodeRelatedRef, PlatformMap, Visibility } from "../core/manifest";
import { CatalogTerms } from "../legal/catalogTerms";
import { FetchLike } from "./remoteSource";

/**
 * Loads a catalog and recursively resolves its sub-catalogs into a tree, with
 * cycle detection and a depth cap. Sub-catalog failures are captured as per-node
 * `errors` (graceful degradation) rather than thrown — a broken branch doesn't
 * sink the tree. See docs/011-CATALOG-MODEL.md §2.
 *
 * **Cold-launch performance (two independent fixes, both opt-in/back-compat):**
 * - Sibling sub-catalog references are resolved with **bounded concurrency**
 *   (`mapWithConcurrency`, `SUBCATALOG_CONCURRENCY` workers), not a sequential
 *   `for … await` — a deeply-nested catalog otherwise pays one serial network
 *   round-trip per sub-catalog. Cycle detection is unaffected: `visited` is never
 *   shared/mutated across siblings, only ever copied one level down a specific
 *   branch (`new Set(ctx.visited).add(url)`), so it already represented
 *   *ancestor-path* membership rather than a global "already resolved" set — the
 *   exact property that makes it safe to parallelize AND still lets the same URL
 *   legitimately appear twice as siblings (a "diamond") without a false cycle.
 * - An optional `LoadCatalogOptions.cache` routes every catalog-document fetch
 *   through `fetchCatalogConditional` (`ETag`/`If-None-Match`), so an unchanged
 *   catalog.json/sub-catalog.json comes back as a 304 and reuses the previously
 *   cached parse instead of re-downloading + re-parsing on every cold launch.
 *   Omitted (the default) is byte-for-byte the prior plain-`fetch` behavior.
 */
export interface ResolvedCatalog {
  id: string;
  title: string;
  packs: CatalogPackEntry[];
  subcatalogs: ResolvedCatalog[];
  /** Git-derived rolled-up newest change date (ISO YYYY-MM-DD), from the referencing entry. */
  updated?: string;
  /**
   * Globally unique per-appearance id from the *referencing* `CatalogNodeEntry.placementId`
   * (materials plan 022), mirrored the same way `updated` is. Absent when the entry doesn't
   * carry one (root has none, since there's no referencing entry). See
   * docs/055-STORED-REFS-CANONICAL-IDS.md.
   */
  placementId?: string;
  /**
   * Browse visibility of the *referencing* `CatalogNodeEntry` (docs/CONTENT-CAPABILITY-
   * FLAGS.md §3), mirrored the same way `updated` is — a tree-building consumer
   * (`content/catalogTree.ts`) prunes `hidden`/`private` children by this field.
   */
  visibility?: Visibility;
  /**
   * Platform availability opt-out (materials plan 060; docs/207-PLATFORM-AVAILABILITY-
   * FILTER.md) — unlike `visibility` (which mirrors only the *referencing entry's* value,
   * dropping the referenced document's own), this is the MERGE (most-restrictive-wins,
   * per key) of BOTH the resolved catalog document's own `Catalog.platforms` and the
   * referencing `CatalogNodeEntry.platforms` (Open Q6 of docs/207) — a deliberate, small
   * divergence from the `visibility` precedent, since a genuinely per-client opt-out
   * should apply regardless of which side of the reference declared it. Root has no
   * referencing entry, so only its own document's map (if any) is ever present here.
   */
  platforms?: PlatformMap;
  /**
   * Plain-text blurb about this catalog, mirrored from the resolved `Catalog.description`
   * (this node's own document, never a parent/child's — docs/047-CONTENT-DESCRIPTIONS.md).
   */
  description?: string;
  /**
   * Display preference mirrored from the resolved `Catalog.showSourceUrl` (this node's own
   * document — docs/085-MANAGE-CATALOGS-PAGE.md). Omitted/`true` = show the catalog's
   * hosting URL in "Manage catalogs"; `false` = show a muted "hidden source" label instead.
   */
  showSourceUrl?: boolean;
  /**
   * Filter axes mirrored from the resolved `Catalog.facets` (this node's own document —
   * docs/195-CATALOG-ENTRY-FILTER-CHIPS.md v0, materials plan 204). v0 reads this only as
   * an **opt-out signal** for the auto Language chip row (an explicit `facets: []`
   * suppresses it); rendering chip groups *from* declared facets is v1.
   */
  facets?: CatalogFacet[];
  /**
   * Overridable count-line nouns, mirrored from the resolved `Catalog.countLabels`
   * (this node's own document, never a parent/child's — docs/
   * 260-OVERRIDABLE-COUNT-LABEL-NOUNS.md v0). Same mirror point as `facets`/
   * `description`/`showSourceUrl` (a node's-own-document property, not one that
   * follows the referencing entry).
   */
  countLabels?: CountLabels;
  /**
   * This catalog document's own terms-of-usage, mirrored from the resolved
   * `Catalog.terms` (this node's own document, never a parent/child's —
   * docs/297-PROVIDER-CATALOG-TERMS-ACCEPTANCE.md v0) — same mirror point as
   * `description`/`showSourceUrl`/`countLabels` above.
   */
  terms?: CatalogTerms;
  /**
   * Node-level "Related courses" see-also, mirrored from the *referencing*
   * `CatalogNodeEntry.related` (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) — same mirror
   * point as `placementId` (present on both a success AND a failure/error-placeholder
   * resolve, unlike `description`, which is the resolved DOCUMENT's own field). Root has
   * none (no referencing entry).
   */
  related?: NodeRelatedRef[];
  /**
   * Opaque cross-placement equality key, mirrored from the *referencing*
   * `CatalogNodeEntry.sameAs` (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0) — same mirror
   * point as `placementId`/`related` above. Root has none.
   */
  sameAs?: string;
  /**
   * Free-form author-declared section label, mirrored from the *referencing*
   * `CatalogNodeEntry.group` (docs/249-CATALOG-GROUPING-AND-SECTIONS.md v0) — same
   * mirror point as `placementId`/`related`/`sameAs` above. Root has none.
   */
  group?: string;
  /**
   * Author-declared course level, mirrored from the *referencing* `CatalogNodeEntry.
   * level` (docs/249-CATALOG-GROUPING-AND-SECTIONS.md v0) — same mirror point as
   * `placementId`/`related`/`sameAs`/`group` above. Root has none.
   */
  level?: number;
  /**
   * How to remove/block this catalog, when it's a **root** the user added themselves (a
   * URL via `CatalogStore` or inline/imported JSON via `InlineCatalogStore`) — omitted for
   * the bundled sample, curated preset catalogs (`appConfig.catalogs`), and content-provider
   * catalogs (managed by signing out, not Remove/Block). Only `useLoadCatalogTree` (the one
   * place that knows which source list a root came from) ever sets this; a sub-catalog
   * resolved recursively via `resolveCatalog` never carries it, so it naturally stays
   * root-only once mirrored onto a `CatalogNode` (long-press "Remove catalog").
   */
  removableSource?: { kind: "url"; url: string } | { kind: "inline"; id: string };
  /**
   * The originating content provider's stable id, set **app-side only** (never parsed
   * from any manifest) on a signed-in provider's aggregating root node
   * (`useLoadCatalogTree`'s provider loop, docs/094-CONTENT-PROVIDER-ACCOUNTS.md) —
   * docs/276-GROUP-BY-PROVIDERS.md v0's load-bearing finding: today's ONLY provider
   * identity is the synthetic `provider:<id>` node id + `title`, which is fragile to
   * group off; this is the real seam, mirroring `group`/`level`/`sameAs`. Deliberately
   * **never propagated to a provider's own descendants** — "Group by: Providers" is
   * inherently a top-level concept (see `catalogGrouping.ts` `groupByProvider`'s own
   * doc comment). Absent for every non-provider root and for every sub-catalog.
   */
  providerId?: string;
  /**
   * The provider's display name (`Provider.name`), mirrored alongside `providerId` —
   * used verbatim (never an i18n key) as the "Group by: Providers" section title. Same
   * root-only, app-side-only scope as `providerId`.
   */
  providerName?: string;
  /**
   * The URL this **root** was loaded from, when it has one — set on every URL-sourced root
   * (curated preset/starter `appConfig.catalogs`/`starterCatalogUrl`, a user-added
   * `catalogStore` URL, and a signed-in provider root backed by exactly one granted catalog),
   * NOT just the user-added ones `removableSource` already covers. Purely an **identity
   * matching handle** for docs/240-PERMALINK-ADD-OR-VIEW-UX.md's broadened
   * `findNodeByCatalogUrl` — a catalog `beequipped://catalog?url=…` permalink needs to resolve
   * "is this already added?" against every source, not only `removableSource.kind==="url"`
   * roots. Display/removal/everything else keeps reading `removableSource` unchanged (a preset
   * catalog stays non-removable even though it now carries an `originUrl`). Absent for
   * inline/imported/authored roots (no hosted URL exists to match) and a multi-catalog
   * provider root (no single URL to attribute it to) — see the doc's own open question. Only
   * `useLoadCatalogTree` sets this; never appears below the root level.
   */
  originUrl?: string;
  /**
   * The URL of the **referencing** `CatalogNodeEntry` that resolved this catalog document —
   * set for ANY URL-fetched catalog, root or sub-catalog, unlike `originUrl` (root-only,
   * a different identity-matching concern for docs/240). Absent for `entry.inline` catalogs
   * (no URL) and for the top-level document passed directly to `loadCatalog`/
   * `loadCatalogFromUrl` (no referencing entry — a caller that already knows that URL, e.g.
   * `apps/admin`'s `useCatalogTree`, mirrors it onto the root the same way `useLoadCatalogTree`
   * mirrors `originUrl`). The one consumer today is the admin app's grant-`ref` derivation
   * (`content/grantRef.ts` `deriveGrantRef`, docs/263-PROVIDER-PAGES-AND-INVITE-ACCESS.md) —
   * a catalog's own declared `id` need not match its actual R2 path segment, so a grant ref
   * must be derived from the real fetch URL, not the id.
   */
  entryUrl?: string;
  /**
   * Set when THIS node's own catalog document failed to load/parse (a bad URL, an offline
   * host, malformed JSON) — the raw error message. Distinct from `errors` (problems
   * resolving this node's *children*): a `loadError` node is a placeholder for a catalog
   * that couldn't be fetched at all, so it carries no packs/subcatalogs of its own. The
   * browse UI renders it as an ordinary row with a red "Error" badge (instead of a wall of
   * raw text) and surfaces the message on long-press + on the node's own screen; the
   * diagnostics log records it (docs/141-ERROR-VISIBILITY-AND-LOG.md,
   * docs/107-CATALOG-UPDATE-DETECTION.md graceful-degradation).
   */
  loadError?: string;
  /** Problems resolving this node's children (cycle, depth) that aren't a whole failed
   * child of their own — a failed child is instead materialized as a `loadError` node
   * above, so it can carry a red "Error" row badge rather than a raw wall line. */
  errors: string[];
}

/**
 * A slash-free, stable node id for a **root** catalog that failed to load by URL, so the
 * error placeholder still slots into the path-addressed tree (`content/catalogTree` builds
 * `path` by slash-joining ids and `findNode` splits on "/", so a raw-URL id would be
 * unaddressable). Sub-catalog error nodes reuse their entry's own `id` (always present on a
 * `CatalogNodeEntry`); only bare root URLs — which have no entry id — need this.
 */
export function catalogErrorNodeId(url: string): string {
  const slug = url.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return `error:${slug}`;
}

export interface LoadCatalogOptions {
  fetchFn?: FetchLike;
  /**
   * Max nesting depth for sub-catalogs (default 25 — deliberately generous; real cycles are
   * already caught by the `visited` URL set above, so this cap only guards against a truly
   * pathological/malformed catalog, not a legitimately deep user taxonomy).
   */
  maxDepth?: number;
  /** Render language; when non-default, prefer `catalog.<lang>.json` (localized titles). */
  lang?: string;
  /**
   * When the URL turns out to be a **pack manifest** (not a catalog), auto-wrap it into a
   * one-pack catalog (docs/011 — "add a pack by its manifest URL"). Default **true**. The
   * provider loader (`providers/providerCatalogLoader.ts`) sets this **false** so a lone
   * pack grant stays a flat `pack` entry (its own `loadPackEntry` fallback) rather than a
   * one-pack subcatalog node.
   */
  wrapPackManifest?: boolean;
  /**
   * Conditional-request cache (perf fix) — when supplied, every catalog document this
   * call fetches (the root, and every sub-catalog it recurses into) is fetched via
   * `fetchCatalogConditional` using this cache's stored `etag`/`lastModified`, so an
   * unchanged catalog.json/sub-catalog.json comes back 304 (no body downloaded, no
   * re-parse) instead of a full re-download + re-parse on every cold launch. Omitted
   * (the default) preserves the exact prior plain-`fetch` behavior — no existing call
   * site is affected unless it opts in. See {@link CatalogFetchCache}.
   */
  cache?: CatalogFetchCache;
  /**
   * The conditional-capable fetch used when `cache` is supplied. Defaults to a plain
   * `fetch(url, init)` wrapper — real `fetch`'s `Response` already satisfies
   * `ConditionalFetchLike` (headers request/response). Kept separate from `fetchFn`
   * (the narrower `(url) => …` signature used everywhere else in this file) so that
   * type doesn't need widening for every other call site; only worth overriding in
   * tests.
   */
  conditionalFetchFn?: ConditionalFetchLike;
}

/** Fetch + validate a single catalog document from a URL. */
export async function fetchCatalog(
  url: string,
  fetchFn: FetchLike = (u) => fetch(u),
): Promise<ParseResult<Catalog>> {
  let text: string;
  try {
    const res = await fetchFn(url);
    if (!res.ok) return err([`failed to fetch catalog (HTTP ${res.status})`]);
    text = await res.text();
  } catch (e) {
    return err([`could not load catalog: ${(e as Error).message}`]);
  }
  return parseCatalogJson(text);
}

/**
 * Fetch signature for {@link fetchCatalogConditional} — a superset of `FetchLike` that
 * also accepts request headers (`If-None-Match`/`If-Modified-Since`) and exposes response
 * headers (`etag`/`last-modified`). Kept separate from `FetchLike` (used broadly by
 * `fetchCatalog`/`loadCatalog*`/`registryLoader`/`providerCatalogLoader`) so this
 * conditional-request addition touches none of those existing call sites or their test
 * fixtures. A real `fetch()` `Response` satisfies this shape.
 */
export type ConditionalFetchLike = (
  url: string,
  init?: { headers?: Record<string, string> },
) => Promise<{
  ok: boolean;
  status: number;
  text(): Promise<string>;
  headers?: { get(name: string): string | null };
}>;

/**
 * Outcome of a conditional catalog fetch (docs/107-CATALOG-UPDATE-DETECTION.md v1):
 * - `"not-modified"` — the server confirmed (HTTP 304) nothing changed since the
 *   `etag`/`lastModified` the caller already had; no body was requested/parsed.
 * - `"fetched"` — a normal 200 with a parsed `catalog` and the response's own cache
 *   validators (`etag`/`lastModified`) to remember for next time, when present.
 * - `"error"` — network failure, a non-2xx/304 status, or a JSON parse failure
 *   (`errors` mirrors `fetchCatalog`'s messages).
 */
export interface ConditionalCatalogFetch {
  status: "not-modified" | "fetched" | "error";
  catalog?: ParseResult<Catalog>;
  etag?: string;
  lastModified?: string;
  errors?: string[];
}

/**
 * Conditional-request variant of {@link fetchCatalog}: sends `If-None-Match`/
 * `If-Modified-Since` when a prior `etag`/`lastModified` is supplied, and treats a 304
 * response as "nothing changed" without re-parsing the body — the cheap check a poller
 * needs before doing real work (docs/107-CATALOG-UPDATE-DETECTION.md v1 "Cheap checks").
 * Injectable `fetchFn` keeps this unit-testable with a fake that never touches the
 * network.
 */
export async function fetchCatalogConditional(
  url: string,
  opts: { etag?: string; lastModified?: string; fetchFn?: ConditionalFetchLike } = {},
): Promise<ConditionalCatalogFetch> {
  const fetchFn = opts.fetchFn ?? ((u, init) => fetch(u, init));
  const headers: Record<string, string> = {};
  if (opts.etag) headers["If-None-Match"] = opts.etag;
  if (opts.lastModified) headers["If-Modified-Since"] = opts.lastModified;

  let res;
  try {
    res = await fetchFn(url, Object.keys(headers).length > 0 ? { headers } : undefined);
  } catch (e) {
    return { status: "error", errors: [`could not load catalog: ${(e as Error).message}`] };
  }

  if (res.status === 304) {
    return { status: "not-modified", etag: opts.etag, lastModified: opts.lastModified };
  }
  if (!res.ok) {
    return { status: "error", errors: [`failed to fetch catalog (HTTP ${res.status})`] };
  }

  const text = await res.text();
  return {
    status: "fetched",
    catalog: parseCatalogJson(text),
    etag: res.headers?.get("etag") ?? undefined,
    lastModified: res.headers?.get("last-modified") ?? undefined,
  };
}

/**
 * The last successfully parsed catalog document fetched from one exact URL, plus the
 * HTTP cache validators that fetch returned — what {@link CatalogFetchCache} stores
 * per URL. A localized variant (`catalog.<lang>.json`) is a *different* URL from its
 * base `catalog.json`, so the two cache independently — no explicit language key
 * needed on top.
 */
export interface CachedCatalogFetch {
  etag?: string;
  lastModified?: string;
  catalog: Catalog;
}

/**
 * Per-URL cache of the last successfully parsed catalog document + its HTTP cache
 * validators (perf: docs/107-CATALOG-UPDATE-DETECTION.md's conditional-request
 * machinery, `fetchCatalogConditional`, existed already but was wired only into the
 * background poller — never the foreground tree load, so a cold launch re-downloaded
 * *every* catalog.json/sub-catalog.json even when nothing changed). Supplying a
 * `CatalogFetchCache` via `LoadCatalogOptions.cache` routes every catalog fetch this
 * loader makes (root + every sub-catalog it recurses into) through
 * `fetchCatalogConditional`: an unchanged document comes back 304 and reuses the
 * cached `catalog` here — no body downloaded, no re-parse.
 *
 * Deliberately its **own** store, not a reuse of `storage/catalogUpdateStore.ts`'s
 * `CatalogUpdateStore` — that one is scoped to user-added **root** catalogs only (it
 * feeds the "Updated" badge, keyed by fingerprints of the packs inside), never a
 * sub-catalog URL, and it stores fingerprints, not the parsed document a 304 needs to
 * hand back verbatim.
 */
export interface CatalogFetchCache {
  get(url: string): Promise<CachedCatalogFetch | null>;
  set(url: string, entry: CachedCatalogFetch): Promise<void>;
}

/** In-memory fake — no persistence. Also usable as a plain same-session cache when a
 * caller doesn't need the result to survive a relaunch. */
export class InMemoryCatalogFetchCache implements CatalogFetchCache {
  private byUrl = new Map<string, CachedCatalogFetch>();

  async get(url: string): Promise<CachedCatalogFetch | null> {
    return this.byUrl.get(url) ?? null;
  }

  async set(url: string, entry: CachedCatalogFetch): Promise<void> {
    this.byUrl.set(url, entry);
  }
}

/** Resolved, always-present form of the optional `cache`/`conditionalFetchFn` pair on
 * `LoadCatalogOptions` — computed once per `loadCatalog`/`loadCatalogFromUrl` call via
 * {@link resolveCacheDeps} and threaded down through `ResolveCtx`. */
interface CacheDeps {
  cache: CatalogFetchCache;
  conditionalFetchFn: ConditionalFetchLike;
}

function resolveCacheDeps(opts: LoadCatalogOptions): CacheDeps | undefined {
  if (!opts.cache) return undefined;
  return {
    cache: opts.cache,
    conditionalFetchFn: opts.conditionalFetchFn ?? ((u, init) => fetch(u, init)),
  };
}

/**
 * Cache-aware fetch of a single catalog document: with `cacheDeps`, sends the cached
 * `etag`/`lastModified` (if any) as conditional headers and, on a 304, hands back the
 * previously cached parse WITHOUT re-parsing a body — the crux of the conditional-
 * request perf fix. A cache miss / genuine 200 falls through to a normal parse (and
 * updates the cache). No `cacheDeps` at all (the default) is a plain `fetchCatalog`,
 * byte-for-byte the prior behavior — every existing caller that doesn't opt in is
 * unaffected.
 */
async function fetchCatalogCached(
  url: string,
  fetchFn: FetchLike,
  cacheDeps: CacheDeps | undefined,
): Promise<ParseResult<Catalog>> {
  if (!cacheDeps) return fetchCatalog(url, fetchFn);

  const prior = await cacheDeps.cache.get(url);
  const res = await fetchCatalogConditional(url, {
    etag: prior?.etag,
    lastModified: prior?.lastModified,
    fetchFn: cacheDeps.conditionalFetchFn,
  });

  if (res.status === "not-modified") {
    // A 304 only ever comes back when we SENT a validator, and we only ever send one
    // when `prior` exists — so `prior` is always set here in practice. The `??` is a
    // defensive backstop (never serve "nothing" on a confirmed-unchanged response),
    // not an expected path.
    if (prior) return ok(prior.catalog);
    return fetchCatalog(url, fetchFn);
  }
  if (res.status === "error") {
    return err(res.errors ?? ["failed to fetch catalog"]);
  }
  // status === "fetched" — a normal 200, either the first time or because it changed.
  const parsed = res.catalog ?? err(["failed to fetch catalog"]);
  if (parsed.ok) {
    await cacheDeps.cache.set(url, { etag: res.etag, lastModified: res.lastModified, catalog: parsed.value });
  }
  return parsed;
}

/**
 * Fetch a catalog, preferring the localized variant (`catalog.<lang>.json`) and
 * falling back to the base URL when it's absent — so titles localize without
 * hiding a catalog that hasn't been translated.
 */
async function fetchCatalogLocalized(
  url: string,
  lang: string | undefined,
  fetchFn: FetchLike,
  cacheDeps?: CacheDeps,
): Promise<ParseResult<Catalog>> {
  if (lang && lang !== DEFAULT_LANGUAGE) {
    const localized = await fetchCatalogCached(localizedFile(url, lang), fetchFn, cacheDeps);
    if (localized.ok) return localized;
  }
  return fetchCatalogCached(url, fetchFn, cacheDeps);
}

interface ResolveCtx {
  fetchFn: FetchLike;
  maxDepth: number;
  depth: number;
  visited: Set<string>;
  lang?: string;
  cacheDeps?: CacheDeps;
}

/**
 * Bounded-concurrency count for sibling sub-catalog resolution (perf fix — a
 * deliberately small pool, not `Promise.all` over every sibling, so a very wide
 * catalog level doesn't open hundreds of connections at once). Exported so tests can
 * assert the pool bound precisely rather than hardcoding it twice.
 */
export const SUBCATALOG_CONCURRENCY = 6;

/**
 * Bounded-concurrency map that preserves `items`' order in the returned array
 * regardless of which item's `worker` settles first — a fixed-size pool of `run`
 * loops, each claiming the next unclaimed index and writing its result to that exact
 * slot (never `.push()`ed), the same "index-addressed, not append" trick
 * `rootLoadBatch.ts` uses for roots. Mirrors `bible/download.ts`'s `downloadPaths`
 * worker-pool shape.
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;

  async function run(): Promise<void> {
    while (cursor < items.length) {
      const i = cursor++;
      results[i] = await worker(items[i], i);
    }
  }

  const workerCount = Math.min(concurrency, items.length) || 1;
  await Promise.all(Array.from({ length: workerCount }, run));
  return results;
}

/**
 * Merge two optional platform opt-out maps into one, most-restrictive-wins per key
 * (docs/207-PLATFORM-AVAILABILITY-FILTER.md Open Q6) — used to combine a resolved
 * catalog document's own `platforms` with the *referencing* `CatalogNodeEntry.platforms`
 * onto a single `ResolvedCatalog.platforms`, so a later `resolvePlatforms(current,
 * rc.platforms)` call at tree-build time consults both levels without needing to carry
 * them separately. `undefined` in, `undefined` out (both absent ⇒ no map at all).
 */
function mergePlatformMaps(...maps: (PlatformMap | undefined)[]): PlatformMap | undefined {
  let merged: PlatformMap | undefined;
  for (const map of maps) {
    if (!map) continue;
    merged ??= {};
    for (const [key, value] of Object.entries(map)) {
      merged[key] = merged[key] === false || value === false ? false : value;
    }
  }
  return merged;
}

/**
 * Resolve one `kind:"catalog"` entry (inline or fetched-by-URL) into its
 * `ResolvedCatalog`, mirroring the referencing entry's own `updated`/`visibility`/
 * `placementId`/merged `platforms` onto it — the per-entry unit of work
 * `mapWithConcurrency` runs in the bounded pool below. Split out of `resolveCatalog`
 * so that function's per-level classification loop (packs vs. depth/cycle-rejected vs.
 * needs-resolving) stays fully synchronous, and only the actual resolving is
 * concurrent.
 */
async function resolveSubEntry(entry: CatalogNodeEntry, ctx: ResolveCtx): Promise<ResolvedCatalog> {
  if (entry.inline) {
    const sub = await resolveCatalog(entry.inline, { ...ctx, depth: ctx.depth + 1 });
    return {
      ...sub,
      updated: entry.updated,
      visibility: entry.visibility,
      placementId: entry.placementId,
      platforms: mergePlatformMaps(sub.platforms, entry.platforms),
      related: entry.related,
      sameAs: entry.sameAs,
      group: entry.group,
      level: entry.level,
    };
  }

  const url = entry.url as string;
  const parsed = await fetchCatalogLocalized(url, ctx.lang, ctx.fetchFn, ctx.cacheDeps);
  if (!parsed.ok) {
    // Materialize the failed child as a placeholder `loadError` node (kept in the tree)
    // rather than a raw wall line on the parent — so the browse UI can render it as an
    // ordinary row with a red "Error" badge, reveal the detail on long-press, and show
    // it on the node's own screen when opened. Its `id` is the entry's own id (always
    // present, slash-free), so it stays path-addressable.
    return {
      id: entry.id,
      title: entry.title,
      packs: [],
      subcatalogs: [],
      loadError: `could not load "${entry.title}": ${parsed.errors.join("; ")}`,
      updated: entry.updated,
      visibility: entry.visibility,
      platforms: entry.platforms,
      placementId: entry.placementId,
      description: entry.description,
      related: entry.related,
      sameAs: entry.sameAs,
      group: entry.group,
      level: entry.level,
      entryUrl: url,
      errors: [],
    };
  }

  const sub = await resolveCatalog(parsed.value, {
    ...ctx,
    depth: ctx.depth + 1,
    // A NEW copy, one level down THIS branch only — never mutates `ctx.visited`
    // itself, so a sibling entry (including one referencing this exact same `url`, a
    // "diamond") is completely unaffected. This is what makes `visited` an
    // ancestor-path check rather than a global "already resolved" set, and is
    // exactly what still makes it safe once siblings resolve concurrently below.
    visited: new Set(ctx.visited).add(url),
  });
  return {
    ...sub,
    updated: entry.updated,
    visibility: entry.visibility,
    placementId: entry.placementId,
    platforms: mergePlatformMaps(sub.platforms, entry.platforms),
    related: entry.related,
    sameAs: entry.sameAs,
    group: entry.group,
    level: entry.level,
    entryUrl: url,
  };
}

async function resolveCatalog(catalog: Catalog, ctx: ResolveCtx): Promise<ResolvedCatalog> {
  const packs: CatalogPackEntry[] = [];
  const errors: string[] = [];
  // Entries this level actually needs to resolve (inline or by URL) — depth-exceeded
  // and cycle-rejected entries are filtered out HERE, synchronously, against the
  // untouched `ctx.visited`/`ctx.depth` (no `await` has happened yet at this level),
  // so classification is 100% deterministic regardless of how the resolution below is
  // scheduled. This is also why a "diamond" (two sibling entries with the same `url`)
  // still resolves both times: neither sibling's presence in `toResolve` depends on
  // the other, since `ctx.visited` is read-only at this level (only ever copied, per
  // branch, inside `resolveSubEntry`).
  const toResolve: CatalogNodeEntry[] = [];

  for (const entry of catalog.entries) {
    if (entry.kind === "pack") {
      packs.push(entry);
      continue;
    }
    if (ctx.depth >= ctx.maxDepth) {
      errors.push(`reached max catalog depth at "${entry.title}"`);
      continue;
    }
    if (!entry.inline) {
      const url = entry.url as string;
      if (ctx.visited.has(url)) {
        errors.push(`skipped circular catalog reference: ${url}`);
        continue;
      }
    }
    toResolve.push(entry);
  }

  // Bounded-concurrency resolution (perf fix — this loop used to be a sequential
  // `for … await`, the dominant cold-launch latency on a deeply-nested catalog: one
  // serial network round-trip per sub-catalog). `mapWithConcurrency` preserves
  // `toResolve`'s order in `subcatalogs` regardless of which entry's fetch settles
  // first — see its own doc comment for how ("index-addressed, never `.push()`ed").
  const subcatalogs = await mapWithConcurrency(toResolve, SUBCATALOG_CONCURRENCY, (entry) =>
    resolveSubEntry(entry, ctx),
  );

  return {
    id: catalog.id,
    title: catalog.title,
    packs,
    subcatalogs,
    description: catalog.description,
    showSourceUrl: catalog.showSourceUrl,
    facets: catalog.facets,
    countLabels: catalog.countLabels,
    terms: catalog.terms,
    // This catalog document's own opt-out map (merged with the referencing entry's, if
    // any, by the caller above via `mergePlatformMaps`) — see the `platforms` doc comment
    // on `ResolvedCatalog`.
    platforms: catalog.platforms,
    errors,
  };
}

/** Resolve an already-parsed catalog (e.g. inline / imported JSON). */
export async function loadCatalog(
  catalog: Catalog,
  opts: LoadCatalogOptions = {},
): Promise<ResolvedCatalog> {
  return resolveCatalog(catalog, {
    fetchFn: opts.fetchFn ?? ((u) => fetch(u)),
    maxDepth: opts.maxDepth ?? 25,
    depth: 0,
    visited: new Set(),
    lang: opts.lang,
    cacheDeps: resolveCacheDeps(opts),
  });
}

/**
 * On a catalog-parse failure, re-read the URL and see if it's actually a **pack manifest**;
 * if so, wrap it into a one-pack catalog (`packManifestToCatalog`) referencing this URL.
 * Returns `null` when it isn't a pack manifest (so the caller surfaces the original catalog
 * error). A second fetch only on the failure path keeps the common (real-catalog) path
 * untouched.
 */
async function tryPackManifestAtUrl(url: string, fetchFn: FetchLike): Promise<Catalog | null> {
  try {
    const res = await fetchFn(url);
    if (!res.ok) return null;
    return packManifestToCatalog(JSON.parse(await res.text()), url);
  } catch {
    return null;
  }
}

/**
 * Fetch a catalog from a URL and resolve its tree (preferring localized variants). When the
 * URL points at a **pack manifest** rather than a catalog (docs/011 — the common "I gave it
 * a manifest.json" case), it's auto-wrapped into a one-pack catalog referencing that URL
 * (`packManifestToCatalog`), so "add a pack by its manifest URL" just works — the pack's
 * lesson files resolve relative to the manifest URL as usual.
 */
export async function loadCatalogFromUrl(
  url: string,
  opts: LoadCatalogOptions = {},
): Promise<ParseResult<ResolvedCatalog>> {
  const fetchFn = opts.fetchFn ?? ((u) => fetch(u));
  const cacheDeps = resolveCacheDeps(opts);
  const parsed = await fetchCatalogLocalized(url, opts.lang, fetchFn, cacheDeps);
  const catalog = parsed.ok
    ? parsed.value
    : opts.wrapPackManifest === false
      ? null
      // The pack-manifest-wrap fallback is a SECOND, plain (uncached) fetch of the same
      // URL, only reached once the (possibly cached) attempt above already failed to
      // parse as a catalog — deliberately outside the conditional-cache path, since a
      // 304 could never legitimately apply to a request that hasn't succeeded yet.
      : await tryPackManifestAtUrl(url, fetchFn);
  if (!catalog) return parsed as ParseResult<ResolvedCatalog>;
  const resolved = await resolveCatalog(catalog, {
    fetchFn,
    maxDepth: opts.maxDepth ?? 25,
    depth: 0,
    visited: new Set([url]),
    lang: opts.lang,
    cacheDeps,
  });
  return ok(resolved);
}
